import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import {
  Card,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Folder as FolderIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Movie as MovieIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { API } from "../../config/env";

const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const VideoCard = styled(Card)`
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.isSelected ? '#4880FF' : 'transparent'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.isSelected ? '#4880FF' : '#e0e0e0'};
  }
`;

const VideoThumbnail = styled.div`
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background-color: #f5f5f5;
  border-radius: 4px 4px 0 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbnailFallback = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  color: #999;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const VideoInfo = styled.div`
  padding: 16px;
`;

const SelectionCheckbox = styled(Checkbox)`
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
  }
`;

const BatchesSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
`;

const BatchChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background-color: ${props => props.active ? '#4880FF' : '#f5f5f5'};
  color: ${props => props.active ? '#fff' : '#333'};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.active ? '#4880FF' : '#e0e0e0'};

  &:hover {
    background-color: ${props => props.active ? '#4880FF' : '#e0e0e0'};
    transform: translateY(-1px);
  }

  svg {
    margin-right: 8px;
  }
`;

const SelectAllButton = styled(Button)`
  text-transform: none;
  font-weight: 600;
`;

const NoVideosMessage = styled.div`
  text-align: center;
  padding: 48px;
  color: #666;
  background: #f9f9f9;
  border-radius: 8px;
  margin-top: 24px;
`;

const UniversitySection = styled.div`
  margin-bottom: 32px;
`;

const UniversityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const UniversityCard = styled(Paper)`
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.isSelected ? '#4880FF' : 'transparent'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .university-icon {
    font-size: 48px;
    color: #4880FF;
    margin-bottom: 16px;
  }
`;

const FileManager = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('unalloted');
  const [anchorEl, setAnchorEl] = useState(null);
  const [openBatchDialog, setOpenBatchDialog] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [universities, setUniversities] = useState(['boston', 'usc', 'texas']); // Add your universities here
  const [selectedUniversity, setSelectedUniversity] = useState(null);

  useEffect(() => {
    if (selectedUniversity) {
      fetchVideos();
    }
  }, [selectedUniversity]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API.CORE}/api/v1/video-analytics/university/${selectedUniversity}`);
      const fetchedVideos = response.data.data;
      setVideos(fetchedVideos);

      const uniqueBatches = [...new Set(fetchedVideos.map(video => video.batch).filter(Boolean))];
      setBatches(uniqueBatches);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const handleUniversitySelect = (university) => {
    setSelectedUniversity(university);
    setSelectedVideos([]); // Clear selected videos when changing university
    setSelectedBatch('unalloted'); // Reset to unalloted when changing university
  };

  const handleVideoSelect = (videoId) => {
    setSelectedVideos(prev => {
      if (prev.includes(videoId)) {
        return prev.filter(id => id !== videoId);
      }
      return [...prev, videoId];
    });
  };

  const handleCardClick = (videoId, event) => {
    // Prevent triggering when clicking on checkbox
    if (!event.target.closest('.MuiCheckbox-root')) {
      handleVideoSelect(videoId);
    }
  };

  const handleSelectAll = () => {
    if (selectedVideos.length === filteredVideos.length) {
      setSelectedVideos([]);
    } else {
      setSelectedVideos(filteredVideos.map(video => video.video_id));
    }
  };

  const handleBatchSelect = (batch) => {
    setSelectedBatch(batch);
    setSelectedVideos([]); // Clear selection when changing batch
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateBatch = () => {
    setOpenBatchDialog(true);
    handleMenuClose();
  };

  const handleBatchDialogClose = () => {
    setOpenBatchDialog(false);
    setNewBatchName('');
  };

  const handleBatchCreate = async () => {
    if (newBatchName && selectedVideos.length > 0) {
      try {
        await axios.put(`${API.CORE}/api/v1/video-analytics/update-batch`, {
          videoIds: selectedVideos,
          batch: newBatchName,
        });
        
        fetchVideos();
        setSelectedVideos([]);
        handleBatchDialogClose();
      } catch (error) {
        console.error('Error creating batch:', error);
      }
    }
  };

  const filteredVideos = selectedBatch === 'unalloted'
    ? videos.filter(video => !video.batch)
    : videos.filter(video => video.batch === selectedBatch);

  return (
    <Container>
      <UniversitySection>
        <Typography variant="h4" gutterBottom>
          Select University
        </Typography>
        <UniversityGrid>
          {universities.map((university) => (
            <UniversityCard
              key={university}
              isSelected={selectedUniversity === university}
              onClick={() => handleUniversitySelect(university)}
              elevation={selectedUniversity === university ? 4 : 1}
            >
              <SchoolIcon className="university-icon" />
              <Typography variant="h6" style={{ textTransform: 'capitalize' }}>
                {university}
              </Typography>
            </UniversityCard>
          ))}
        </UniversityGrid>
      </UniversitySection>

      {selectedUniversity && (
        <>
          <Header>
            <Typography variant="h4">File Manager - {selectedUniversity.toUpperCase()}</Typography>
            <HeaderActions>
              <SelectAllButton
                variant="outlined"
                color="primary"
                onClick={handleSelectAll}
                startIcon={selectedVideos.length === filteredVideos.length ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
              >
                {selectedVideos.length === filteredVideos.length ? 'Deselect All' : 'Select All'}
              </SelectAllButton>
              
              <Tooltip title={selectedVideos.length === 0 ? "Select videos to create a batch" : "Create new batch"}>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateBatch}
                    disabled={selectedVideos.length === 0}
                    startIcon={<CreateNewFolderIcon />}
                  >
                    Create Batch
                  </Button>
                </span>
              </Tooltip>
            </HeaderActions>
          </Header>

          <BatchesSection>
            <BatchChip 
              active={selectedBatch === 'unalloted'} 
              onClick={() => handleBatchSelect('unalloted')}
            >
              <FolderIcon />
              Unalloted Videos
            </BatchChip>
            {batches.map(batch => (
              <BatchChip
                key={batch}
                active={selectedBatch === batch}
                onClick={() => handleBatchSelect(batch)}
              >
                <FolderIcon />
                {batch}
              </BatchChip>
            ))}
          </BatchesSection>

          {filteredVideos.length > 0 ? (
            <VideoGrid>
              {filteredVideos.map(video => (
                <VideoCard 
                  key={video.video_id}
                  isSelected={selectedVideos.includes(video.video_id)}
                  onClick={(e) => handleCardClick(video.video_id, e)}
                >
                  <VideoThumbnail>
                    <ThumbnailImage 
                      src={`${video.thumbnail}`}
                      alt={video.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        // Add a fallback element if it doesn't exist
                        if (!e.target.nextElementSibling) {
                          const fallback = document.createElement('div');
                          fallback.className = e.target.parentElement.lastChild.className;
                          fallback.innerHTML = '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/></svg>';
                          e.target.parentElement.appendChild(fallback);
                        }
                      }}
                    />
                    <ThumbnailFallback style={{ display: 'none' }}>
                      <MovieIcon />
                    </ThumbnailFallback>
                    <SelectionCheckbox
                      checked={selectedVideos.includes(video.video_id)}
                      onChange={() => handleVideoSelect(video.video_id)}
                      className="MuiCheckbox-root"
                    />
                  </VideoThumbnail>
                  <VideoInfo>
                    <Typography variant="subtitle1" gutterBottom>
                      {video.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Batch: {video.batch || 'Unassigned'}
                    </Typography>
                  </VideoInfo>
                </VideoCard>
              ))}
            </VideoGrid>
          ) : (
            <NoVideosMessage>
              <Typography variant="h6" gutterBottom>
                No videos found
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {selectedBatch === 'unalloted' 
                  ? 'There are no unalloted videos available.' 
                  : 'No videos in this batch.'}
              </Typography>
            </NoVideosMessage>
          )}

          <Dialog open={openBatchDialog} onClose={handleBatchDialogClose}>
            <DialogTitle>Create New Batch</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Batch Name"
                fullWidth
                value={newBatchName}
                onChange={(e) => setNewBatchName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleBatchDialogClose}>Cancel</Button>
              <Button 
                onClick={handleBatchCreate} 
                color="primary"
                disabled={!newBatchName.trim() || selectedVideos.length === 0}
              >
                Create
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Container>
  );
};

export default FileManager; 
